import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import * as ccUtils from '../CC/utils';
import styles from './Record.module.css';
import MobileZoomedImage from './MobileZoomedImage';
import ImageWithFallback from '../GenericComponents/ImageWithFallback';
import { getImages } from '../Result/getImages';
import ObjAssocMobile from './ObjAssocMobile';

interface RecordProps {
  record: any;
  setAndKeepMainY: (newMainY: number, prevMainY: number) => void;
}

interface Image {
  image: string;
  copyright: string;
  rank: string;
}

function Record({ record, setAndKeepMainY }: RecordProps,) {
  const { t, i18n } = useTranslation();
  const ctx = useCCContext();
  const [isCopyrightModalVisible, setIsCopyrightModalVisible] = useState(false);
  const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
  const filmStripRef = useRef<HTMLDivElement | null>(null);
  const [noImageUrl, setNoImageUrl] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [filmStripImages, setFilmStripImages] = useState<Image[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isZoomedImageVisible, setIsZoomedImageVisible] = useState<boolean>(false);

  const showSearch = () => {
    setAndKeepMainY(-200, -300);
  }

  useEffect(() => {
    if (record) {

      let _imageUrl = ""; //todo: noimage
      const allImages = getImages(record);
      if ((allImages as any)["mobilerecord"]?.length > 0) {
        _imageUrl = (allImages as any)["mobilerecord"][0]?.url;
      }
      setImageUrl(_imageUrl);

      const zoomImageUrl = (allImages as any)["dzi"][0]?.url;
      setZoomUrl(zoomImageUrl);

      if ((allImages as any)["filmstrip"]?.length > 0) {
        setFilmStripImages((allImages as any)["filmstrip"]);
      }

    }
  }, [record]);

  if (!record) {
    return <div>No record selected.</div>;
  }

  const handleScrollLeft = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: -300,
      behavior: 'smooth'
    });
  };

  const handleFilmStripImageClick = (image: Image, idx: number) => {

    const allImages = getImages(record);

    let _imageUrl = ""; //todo: noimage
    if ((allImages as any)["mobilerecord"]?.length > idx) {
      _imageUrl = (allImages as any)["mobilerecord"][idx]?.url;
    }
    setImageUrl(_imageUrl);

    const zoomImageUrl = (allImages as any)["dzi"][idx]?.url;
    setZoomUrl(zoomImageUrl);

    setCurrentIndex(idx);

    /*
    const newImageFilename = replaceExtensionWithDzi(image.image);
    const newImageUrl = `ccImageProxy.ashx?a=1&filename=images/${newImageFilename}`;
    setZoomUrl(newImageUrl);
    setImageUrl(image.image ?
      `ccImageProxy.ashx?filename=images/${image.image}&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes` :
      `ccImageProxy.ashx?filename=images/noimage.cci&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes`);

    setCurrentIndex(idx);
    */
  };

  const handleScrollRight = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: 300,
      behavior: 'smooth'
    });
  };

  function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    let fieldValue = getNestedValue(record?.data?.Record, fieldName);

    //console.log(fieldName, fieldValue); // Add this line to log the value

    if (typeof fieldValue === 'object' && fieldValue !== null) {
      console.warn(`Rendering object as string for field ${fieldName}:`, fieldValue);
      fieldValue = JSON.stringify(fieldValue);
    }

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        <span className={styles.tagValue}>{fieldValue ?? defaultValue}</span>
      </div>
    );
  }

  function getAltNum(record: any, label: string): JSX.Element | null {
    let fields = record?.data?.Record?.ObjAltnum;
    if (!fields) return null;
    if (!Array.isArray(fields)) fields = [fields];

    return (
      <div>
        {fields.map((field: any, index: number) => (
          <div className={styles.row} key={index}>
            <span className={styles.taglabel}>{field.Description} : </span>
            <span className={styles.tagValue}>{field.Altnum}</span>
          </div>
        ))}
      </div>
    );
  }

  interface DefaultRowFoldedProps {
    record: any;
    label: string;
    fieldName: string;
    defaultValue?: string;
  }

  const DefaultRowFolded: React.FC<DefaultRowFoldedProps> = ({ record, label, fieldName, defaultValue = "" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showToggleButton, setShowToggleButton] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    let fieldValue = record?.data?.Record?.[fieldName];

    if (typeof fieldValue === 'object') {
      fieldValue = JSON.stringify(fieldValue);
      if (fieldValue === "{}") fieldValue = "";
    }

    useEffect(() => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const maxHeight = 4.8 * 16;
        setShowToggleButton(contentHeight > maxHeight);
      }
    }, [fieldValue, defaultValue]);

    if (!fieldValue && !defaultValue) return null;

    const toggleExpanded = () => {
      setIsExpanded(!isExpanded);
    };

    const sanitizedHTML = DOMPurify.sanitize(fieldValue ?? defaultValue);

    return (
      <div className={styles.row}>
        {label && <span className={styles.taglabel}>{label} : </span>}
        <div
          ref={contentRef}
          className={`${styles.tagValue} ${isExpanded ? styles.expanded : styles.collapsed}`}
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />
        {showToggleButton && (
          <div className={styles.toggleButtonContainer}>
            <button onClick={toggleExpanded} className={styles.toggleButton}>
              {isExpanded ? '-' : '+'}
            </button>
          </div>
        )}
      </div>
    );
  };

  function getDefaultRowHyperlink(record: any, label: string, fieldName: string, subtag: string = "", defaultValue: string = "") {
    let fieldValue = record?.data?.Record?.[fieldName];

    if (subtag && fieldValue) {
      fieldValue = fieldValue[subtag] || defaultValue;
    }

    const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!subtag) {
        ccUtils.customSearch(ctx, '/Record/' + fieldName, '', '' + value + '', label + ' : ' + value);
      } else {
        ccUtils.customSearch(ctx, '/Record/' + fieldName + '/' + subtag, '', '' + value + '', label + ' : ' + value);
      }
      showSearch();
    };

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        {fieldValue
          ? <span className={styles.hyperlink} onClick={(e) => handleOnClick(label, fieldValue, e)}>{fieldValue}</span>
          : defaultValue}
      </div>
    );
  }

  function getDefaultRowHyperlinkHtml(record: any, label: string, fieldName: string, subtag: string = "", defaultValue: string = "") {
    let fieldValue = record?.data?.Record?.[fieldName];
  
    if (subtag && fieldValue) {
      fieldValue = fieldValue[subtag] || defaultValue;
    }
  
    const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const path = subtag ? `/Record/${fieldName}/${subtag}` : `/Record/${fieldName}`;
      ccUtils.customSearch(ctx, path, '', '' + value + '', `${label} : ${value}`);
    };
  
    if (!fieldValue && !defaultValue) return null;
  
    // Vervang <break> en bereid HTML voor
    const htmlValue = DOMPurify.sanitize((fieldValue || defaultValue).replace(/<break>/g, '<br />'));
  
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        <span
          className={styles.hyperlink}
          onClick={(e) => handleOnClick(label, fieldValue, e)}
          dangerouslySetInnerHTML={{ __html: htmlValue }}
        ></span>
      </div>
    );
  }

  function getDefaultRowHyperlinkWithArray(
    record: any,
    label: string,
    fieldName: string,
    subtag: string = "",
    defaultValue: string = ""
    ) {
      let fieldValues: string[] = [];
    
      // Haal de waarden op
      if (Array.isArray(record?.data?.Record?.[fieldName])) {
        fieldValues = record.data.Record[fieldName];
      } else if (record?.data?.Record?.[fieldName]) {
        fieldValues = [record.data.Record[fieldName]];
      }
    
      // Verwerk subtag als dat nodig is
      if (subtag) {
        fieldValues = fieldValues.map((item: any) => item[subtag]).filter(Boolean);
      }
    
      const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!subtag) {
          ccUtils.customSearch(ctx, `/Record/${fieldName}`, "", value, `${label} : ${value}`);
        } else {
          ccUtils.customSearch(ctx, `/Record/${fieldName}/${subtag}`, "", value, `${label} : ${value}`);
        }
      };
    
      if (fieldValues.length === 0 && !defaultValue) return null;
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          {fieldValues.length > 0 ? (
            fieldValues.map((value: string, index: number) => (
              <div key={index}>
                <span
                  className={styles.hyperlink}
                  onClick={(e) => handleOnClick(label, value, e)}
                >
                  {value}
                </span>
              </div>
            ))
          ) : (
            <div>{defaultValue}</div>
          )}
        </div>
      );
  }

  function getDefaultRowHyperlinkWithArrayAlpha(
    record: any,
    label: string,
    fieldName: string,
    subtag: string = "",
    defaultValue: string = ""
    ) {
      let fieldValues: string[] = [];
    
      // Haal de waarden op
      if (Array.isArray(record?.data?.Record?.[fieldName])) {
        fieldValues = record.data.Record[fieldName];
      } else if (record?.data?.Record?.[fieldName]) {
        fieldValues = [record.data.Record[fieldName]];
      }
    
      // Verwerk subtag als dat nodig is
      if (subtag) {
        fieldValues = fieldValues.map((item: any) => item[subtag]).filter(Boolean);
      }

      // Sorteer alfabetisch
      fieldValues.sort((a, b) => a.localeCompare(b));
    
      const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!subtag) {
          ccUtils.customSearch(ctx, `/Record/${fieldName}`, "", value, `${label} : ${value}`);
        } else {
          ccUtils.customSearch(ctx, `/Record/${fieldName}/${subtag}`, "", value, `${label} : ${value}`);
        }
      };
    
      if (fieldValues.length === 0 && !defaultValue) return null;
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          {fieldValues.length > 0 ? (
            fieldValues.map((value: string, index: number) => (
              <div key={index}>
                <span
                  className={styles.hyperlink}
                  onClick={(e) => handleOnClick(label, value, e)}
                >
                  {value}
                </span>
              </div>
            ))
          ) : (
            <div>{defaultValue}</div>
          )}
        </div>
      );
  } 

  function getDefaultRowHyperlinkTemplated(
    record: any,
    label: string,
    displayFieldName: string,
    searchFieldName: string,
    renderFunction?: (value: any) => { display: string, hyperlink: string },
    defaultValue: string = ""
  ) {

    const fieldValue = record?.data?.Record?.[displayFieldName];
    const isArray = Array.isArray(fieldValue);

    const processValue = (value: any) => {
      return renderFunction && value
        ? renderFunction(value)
        : { display: value, hyperlink: value };
    };

    const values = isArray ? fieldValue.map(processValue) : [processValue(fieldValue)];

    const handleOnClick = (label: string, hyperlink: string, display: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      ccUtils.customSearch(ctx, '/Record/' + searchFieldName, '', '' + hyperlink + '', label + ' : ' + display);
      showSearch();
    };

    if (!values.some(v => v.display) && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        {values.flatMap((v, idx, arr) => {
          const content = (
            <span key={`${displayFieldName}-${idx}`} className={styles.hyperlink} onClick={(e) => handleOnClick(label, v.hyperlink, v.display, e)}>
              {v.display}
            </span>
          );

          if (idx !== arr.length - 1) {
            return [content, ', '];
          } else {
            return [content];
          }
        })}
        {!values.some(v => v.display) && defaultValue}
      </div>
    );
  }

  const renderExposition = (exposition: any) => {
    console.log(exposition);
    if (exposition['DisplayDate']) {
      return { display: exposition['ExhTitle'] + ' (' + exposition['DisplayDate'] + ')', hyperlink: "[" + exposition['ExhTitle'] + "]" };
    } else {
      return { display: exposition['ExhTitle'], hyperlink: "[" + exposition['ExhTitle'] + "]" };
    }
  };

  function getThesaurusRows(record: any, label: string, CN: string, minLength: number) {
    const thesTerms = record?.data?.Record?.ThesTerm;
    if (!thesTerms) return null;

    const terms = Array.isArray(thesTerms) ?
      thesTerms.filter((term: any) => term.CN.startsWith(CN)) :
      [thesTerms].filter((term: any) => term.CN.startsWith(CN));

    const rows = terms.map((term: any, idx) => {
      let termElements: JSX.Element[] = [];
      let firstDone = false;

      term.TermPathPart.forEach((part: any) => {
        if (part.CN && part.CN.length >= minLength) {
          termElements.push(
            <span>
              {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
              <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)}>
                {part.TermPathPart}
              </span>
            </span>
          );
          firstDone = true;
        }
      });

      return (
        <div className={styles.row} key={`${term.CN}-${idx}`}>
          <span className={styles.taglabel}>{label} : </span>
          {termElements}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return rows;
  }

  function getThesaurusRowsByType(record: any, label: string, ThesType: string, minLength: number) {
    const thesTerms = record?.data?.Record?.mqb_thesterm;
    if (!thesTerms) return null;

    const terms = Array.isArray(thesTerms)
      ? thesTerms.filter((term: any) => term.Type.trim() === ThesType)
      : [thesTerms].filter((term: any) => term.Type.trim() === ThesType);

    const rows = terms.map((term: any, idx) => {
      let termElements: JSX.Element[] = [];
      let firstDone = false;

      const processPart = (part: any) => {
        if (part.CN && part.CN.length >= minLength) {
          termElements.push(
            <span>
              {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
              <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TERM)}>
                {part.TERM}
              </span>
            </span>
          );
          firstDone = true;
        }
      };

      if (minLength === -1) {
        processPart(term.thesTerms[term.thesTerms.length - 1]);
      } else {
        term.thesTerms.forEach(processPart);
      }

      let matchingRemarks = "";

      if (ThesType == "_Toponyme") { //20250404 WvW, _ added
        const termCN = term.CN;
        let toponymes = record?.data?.Record?.Toponyme;
        if (toponymes && !Array.isArray(toponymes)) {
          toponymes = [toponymes];
        }

        if (toponymes && Array.isArray(toponymes)) {
          const matchingToponyme = toponymes.find(t => t.CN === termCN && t.Remarks);
          if (matchingToponyme) {
            matchingRemarks = " [" + matchingToponyme.Remarks + "]";
          }
        }
      }

      return (
        <div className={styles.row} key={`${term.CN}-${idx}`}>
          <span className={styles.taglabel}>{label} : </span>
          {termElements}{matchingRemarks}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return rows;
  }

  function getPersonnes(record: any, tag: keyof typeof record.data.Record) {
    const elements = record?.data?.Record?.[tag];
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];

    elementArray.sort((a, b) => {
      const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
      const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
      return keyA.localeCompare(keyB);
    });

    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/Record/ConX/AlphaSort,/Record/ConXother/AlphaSort', '', '[' + name + ']', t('constituent')+' : ' + name);
      showSearch();
    };

    const rows = elementArray.map((element: any, idx) => {
      const { Role, [tag]: tagValue, DisplayDate } = element;
      return (
        <div className={styles.row} key={`${element.ConstituentID}-${idx}`} >
          <span className={styles.taglabel}>{Role} : </span>
          <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
          {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return rows;
  }

  function getPersonnesWithRoleType(record: any, tag: keyof typeof record.data.Record, roleTypes: string[]) {
    const elements = record?.data?.Record?.[tag];
    if (!elements) return null;
  
    const elementArray = Array.isArray(elements) ? elements : [elements];
  
    elementArray.sort((a, b) => {
      const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
      const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
      return keyA.localeCompare(keyB);
    });
  
    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/Record/ConX/AlphaSortConX,/Record/ConXother/AlphaSort', '', '[' + name + ']', t('constituent') + ' : ' + name);
    };
  
    const rows = elementArray.map((element: any, idx) => {
      if (roleTypes.includes(element.RoleTypeID)) {
        const { Role, AlphaSort, AlphaSortConX, DisplayDate, Biography } = element;
  
        // Bouw een record-structuur na die DefaultRowFolded verwacht
        const fakeRecord = {
          data: {
            Record: {
              Biography: Biography
            }
          }
        };
  
        return (
          <div className={styles.row} key={`${element.ConstituentID}-${idx}`}>
            <span className={styles.taglabel}>{Role} : </span>
            <span className={styles.authName} onClick={(e) => handleOnClick(e, AlphaSort || AlphaSortConX)}>
              {AlphaSort || AlphaSortConX}
            </span>
            {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
            {Biography && <DefaultRowFolded record={fakeRecord} label="" fieldName="Biography" />}
          </div>
        );
      }
    });
  
    if (rows.length === 0) return null;
  
    return rows;
  } 

  function getObjectTitle(record: any) {
    const titleData = record?.data?.Record?.Title;

    if (!titleData) return <div className={styles.row}>{t('No title')}</div>;

    if (Array.isArray(titleData)) {
      return titleData.map((data, index) => {
        const title = data.Title;
        const titleType = data.TitleType;

        if (!title) return null;

        return (
          <div key={index} className={styles.row}>
            {titleType ? (
              <>
                <span className={styles.taglabel}>{titleType} :</span> {title}
              </>
            ) : title}
          </div>
        );
      });
    } else {
      const title = titleData.Title;
      const titleType = titleData.TitleType;

      if (!title) return <div className={styles.row}>{t('No title')}</div>;

      return (
        <div className={styles.row}>
          {titleType ? (
            <>
              <span className={styles.taglabel}>{titleType} :</span> {title}
            </>
          ) : title}
        </div>
      );
    }
  }

  function getIconoTitle(record: any) {
    const titleData = record?.data?.Record?.Title;

    if (!titleData) return <div className={styles.row}>{t('No title')}</div>;

    if (Array.isArray(titleData)) {
      return (
        <>
          {titleData.map((data, index) => {
            const title = data.Title;
            const titleType = "Historical title";

            if (!title) return null;

            return (
              <div key={index} className={styles.row}>
                <span className={styles.taglabel}>{t(titleType)} :</span> {title}
              </div>
            );
          })}
        </>
      );
    } else {
      const title = titleData.Title;
      const titleType = "Historical title";

      if (!title) return <div className={styles.row}>{t('No title')}</div>;

      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{t(titleType)} :</span> {title}
        </div>
      );
    }
  }

  function getDownloadAllowed(): boolean {
    return record.data?.Record?.ThesTerm?.some((item: any) => item.CN === "ICO.AAN.AAB.AAC");
  }

  function handleThesaurusNodeClick(event: React.MouseEvent, CN: string, label: string, term: string): void {
    event.stopPropagation();
    event.preventDefault();
    ccUtils.customSearch(ctx, '/Record/mqb_thesterm/thesTerms/CN', '', '[' + CN + ']', label + ': ' + term);
    showSearch();
  }

  function hasFilterValue(record: any, filterField: string, filterValue: string): boolean {
    const fieldData = record?.data?.Record?.[filterField];

    if (!fieldData) return false;

    if (Array.isArray(fieldData)) {
      return fieldData.includes(filterValue);
    }

    return fieldData === filterValue;
  }

  const toggleCopyrightModal = () => {
    setIsCopyrightModalVisible(prevState => !prevState);
  };

  const toggleCommentsModal = () => {
    setIsCommentsModalVisible(prevState => !prevState);
  };


  try {
    return (
      <div id="pdf-container" className={styles.Record}>
        <div className={styles.ccListviewRecordImage}>
          <div className={'pdfOnly'} style={{ marginBottom: '40px' }}><img src='pdflogo.png' /></div>
          <div className={styles.UnzoomedImage}>
            {imageUrl && <ImageWithFallback src={imageUrl} alt="Image" placeholder="ccImageProxy.ashx?filename=images/noimage.cci&width=1024" maxWidth={'90vw'} maxHeight={'60vh'} />}
            <div className={styles.UnzoomedImageExpand} onClick={() => setIsZoomedImageVisible(!isZoomedImageVisible)}>
              {isZoomedImageVisible ?
                (<GenericIconButton
                  icon='solid/compress-alt.svg'
                  hoverEnabled={true}
                  width='28px'
                  height='28px'
                  onClick={() => setIsZoomedImageVisible(false)}
                />) :
                (<GenericIconButton
                  icon='solid/expand-alt.svg'
                  hoverEnabled={true}
                  width='28px'
                  height='28px'
                  onClick={() => setIsZoomedImageVisible(true)}
                />)
              }
            </div>
          </div>
          <div className={styles.ZoomedImage} style={{ display: isZoomedImageVisible ? 'block' : 'none' }}>
            <div className={styles.topRightButton}>
              <GenericIconButton
                icon='solid/compress-alt.svg'
                hoverEnabled={true}
                width='28px'
                height='28px'
                onClick={() => setIsZoomedImageVisible(false)}
              />
            </div>
            {!zoomUrl && noImageUrl && (
              <img
                src={noImageUrl}
                alt="No image available"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '60vh',
                  width: 'auto',
                  height: 'auto',
                  display: 'block'
                }}
              />
            )}
            {zoomUrl && <MobileZoomedImage key={`${zoomUrl}-${isZoomedImageVisible}`} dzi={zoomUrl} downloadAllowed={true} images={filmStripImages.map(img => ({ imagesmore: img.image, copyright: img.copyright, rank: img.rank }))} initialIndex={currentIndex} />}
          </div>
          {filmStripImages.length > 1 && (
            <div className={styles.filmStripContainer}>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-left.svg' width='22px' height='20px' onClick={handleScrollLeft} />
              </div>
              <div className={styles.filmStrip} ref={filmStripRef}>
                {filmStripImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={(img as any).url}
                    alt={`Image rank ${(img as any).rank}`}
                    className={styles.filmStripImage}
                    onClick={() => handleFilmStripImageClick(img, idx)}
                  />
                ))}
              </div>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-right.svg' width='22px' height='20px' onClick={handleScrollRight} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.ccListviewRecordText}>

          {
            record?.data?.Record?.source === "objects" &&
            <>
              {getPersonnesWithRoleType(record, 'ConX', ['1'])}
              {getPersonnesWithRoleType(record, 'ConXother', ['1'])}
              {getObjectTitle(record)}
              {getDefaultRow(record, t("record_dates"), "Dated")}
              {getDefaultRow(record, t("record_date_entry"), "ObjAccession.InitDate")}
              {getThesaurusRowsByType(record, t("record_place"), '_Toponyme', 7)} 
              {getThesaurusRowsByType(record, t("record_culture"), 'Ethnonyme / Style', -1)}
              {getDefaultRowHyperlinkHtml(record, t("record_materials_techniques"), "Medium")}
              {getDefaultRow(record, t("record_dimensions"), "Dimensions")}

              {getDefaultRowHyperlinkWithArrayAlpha(record, "Evénement historique", "Evenement", "Evenement")}
              {getDefaultRowHyperlinkWithArray(record, t("Exposition"), "Expo", "ExhTitle")}

              {getPersonnesWithRoleType(record, 'ConXother', ['2'])}
              {getPersonnesWithRoleType(record, 'ConXother', ['5'])}
              {getDefaultRow(record, t("record_inventory_number"), "ObjectNumber")}
              {getDefaultRow(record, t("record_management_number"), "ObjectNumber2")}

              {record?.data?.Record?.['ObjAltnum'] ? getAltNum(record, t("record_other_numbers")) : ""}

              {(hasFilterValue(record, 'Expose', 'Oui')) && getDefaultRow(record, t("record_exhibited_object"), "ObjLocation.LocationString")}

              <DefaultRowFolded record={record} label={t("record_description")} fieldName="Description" />
              <DefaultRowFolded record={record} label={t("record_inscriptions")} fieldName="Inscribed" />
              <DefaultRowFolded record={record} label={t("record_signature")} fieldName="Signed" />
              <DefaultRowFolded record={record} label={t("record_usage")} fieldName="Creditline" />

              {/* getDefaultRowHyperlinkTemplated(record, t("record_exhibitions"), "Expo", "Expo/ExhTitle", renderExposition) */}

              <ObjAssocMobile record={record} setAndKeepMainY={setAndKeepMainY}></ObjAssocMobile>

            </>
          }

          {
            record?.data?.Record?.source === "icono" &&
            <>
              {getPersonnesWithRoleType(record, 'ConX', ['1'])}
              {getPersonnesWithRoleType(record, 'ConXother', ['1'])}
              {getIconoTitle(record)}
              {getDefaultRow(record, t("record_dates"), "Dated")}
              {getDefaultRow(record, t("record_date_entry"), "ObjAccession.InitDate")}
              {getThesaurusRowsByType(record, t("record_place"), '_Toponyme', 7)}
              {getThesaurusRowsByType(record, t("record_culture"), 'Ethnonyme / Style', -1)}
              {getDefaultRowHyperlink(record, t("record_materials_techniques"), "Medium")}
              {getDefaultRow(record, t("record_dimensions"), "Dimensions")}
              {getDefaultRowHyperlinkWithArray(record, t("record_historical_event"), "Evenement", "Evenement")}
              {getDefaultRowHyperlinkWithArray(record, t("Exposition"), "Expo", "ExhTitle")}
              {getPersonnesWithRoleType(record, 'ConXother', ['2'])}
              {getPersonnesWithRoleType(record, 'ConXother', ['5'])}
              {getDefaultRow(record, t("record_inventory_number"), "ObjectNumber")}
              {getDefaultRow(record, t("record_management_number"), "ObjectNumber2")}
              {record?.data?.Record?.['ObjAltnum'] ? getAltNum(record, t("record_other_numbers")) : ""}
              {(hasFilterValue(record, 'Expose', 'Oui')) && getDefaultRow(record, t("record_exhibited_object"), "ObjLocation.LocationString")}
              <DefaultRowFolded record={record} label={t("record_description")} fieldName="Description" />
              <DefaultRowFolded record={record} label={t("record_inscriptions")} fieldName="Inscribed" />
              <DefaultRowFolded record={record} label={t("record_signature")} fieldName="Signed" />
              <DefaultRowFolded record={record} label={t("record_usage")} fieldName="Creditline" />

              {/* getDefaultRowHyperlinkTemplated(record, t("record_exhibitions"), "Expo", "Expo/ExhTitle",renderExposition) */}

              <ObjAssocMobile record={record} setAndKeepMainY={setAndKeepMainY}></ObjAssocMobile>

            </>
          }

          {record?.data?.Record?.source === "icono" &&
            <div style={{ marginTop: '20px' }}>
              <GenericIconButton icon='light/copyright.svg' width='22px' height='20px' onClick={toggleCopyrightModal} />
              <div className={`${styles.comments} screenOnly`} onClick={toggleCommentsModal}>{t("record_send_comments")}</div>
            </div>
          }


        </div>
      </div>
    );
  } catch (e) {
    setError(e as any);
    return <div>Error: {(e as any).message}</div>;
  }
}

export default Record;
